import { useEffect, useState } from 'react';

/** Returns `true` when the active scroll container is past `threshold`px.
 *
 *  Why TWO listeners (window + main)?
 *  doc-ui's AppShell sets `min-height: 100vh` on the shell and
 *  `overflow-y: auto` on `.vq-app-shell__main`. In tall-content layouts
 *  the actual scroll lands on **`window`** because the shell grows past
 *  100vh; in fixed-height layouts it lands on **`main`** because the
 *  shell stays at exactly 100vh. We don't know which one will fire — so
 *  we attach to both and OR their positions. Whichever ancestor moves,
 *  the chrome flips on cue. The earlier "listen on main only" version
 *  silently no-op'd in production because the page actually scrolled the
 *  window.
 *
 *  Throttled with `requestAnimationFrame` because scroll events fire at
 *  the device's input rate (60–120 Hz on most browsers) and we only need
 *  one state update per paint. The functional setState skips re-renders
 *  when the boolean didn't actually change. */
export function useScrolled(threshold = 4): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const main = document.querySelector<HTMLElement>('.vq-app-shell__main');

    let raf = 0;
    const update = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const winY = window.scrollY;
        const mainY = main?.scrollTop ?? 0;
        const past = winY > threshold || mainY > threshold;
        setScrolled((prev) => (prev === past ? prev : past));
      });
    };

    // Initial probe — back-nav / anchor link / refresh may land us on
    // an already-scrolled page.
    update();

    const targets: (Window | HTMLElement)[] = [window];
    if (main !== null) targets.push(main);
    for (const t of targets) t.addEventListener('scroll', update, { passive: true });

    return () => {
      for (const t of targets) t.removeEventListener('scroll', update);
      cancelAnimationFrame(raf);
    };
  }, [threshold]);

  return scrolled;
}
